import { Component, OnInit, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { AuthService } from '../../../core/auth/auth.service';
import { environment } from '../../../config/environment';
import { StudentPaymentScheduleItem } from '../interfaces/student-payment.interfaces';

@Component({
  selector: 'app-student-account-locked',
  template: `
    <div class="account-locked-container">
      <div class="locked-card">
        <!-- Warning Icon -->
        <mat-icon class="warning-icon">lock</mat-icon>

        <!-- Main Message -->
        <h1>Account Temporarily Locked</h1>
        <p class="subtitle">Your account has been locked due to overdue payments</p>

        <!-- Loading State -->
        <div *ngIf="loading" class="loading-section">
          <mat-spinner diameter="40"></mat-spinner>
          <p>Loading payment information...</p>
        </div>

        <!-- Overdue Payment Card -->
        <mat-card *ngIf="!loading && overduePayments.length > 0" class="overdue-payment-card">
          <h3>Outstanding Payments</h3>
          <div class="payment-item" *ngFor="let item of overduePayments">
            <div class="payment-header">
              <span class="payment-subject">{{ item.subjectName }}</span>
              <span class="payment-amount">{{ item.formattedAmount }}</span>
            </div>
            <div class="payment-due-date">
              <mat-icon class="warning-small">warning</mat-icon>
              <span>Due: {{ item.formattedDueDate }}</span>
              <span class="days-overdue">({{ getDaysOverdue(item) }} days overdue)</span>
            </div>
          </div>
          <mat-divider></mat-divider>
          <div class="total-overdue">
            <span class="total-label">Total Overdue:</span>
            <span class="total-amount">{{ totalOverdueAmount }}</span>
          </div>
        </mat-card>

        <!-- No Payment Data -->
        <mat-card *ngIf="!loading && overduePayments.length === 0" class="info-card">
          <p>Your account has been locked. Please contact the school administration for more information.</p>
        </mat-card>

        <!-- Instructions -->
        <div class="instructions">
          <h3>To Restore Account Access</h3>
          <ol>
            <li>Contact the school administration to make your payment</li>
            <li>Once payment is confirmed, your account will be unlocked</li>
            <li>You will then be able to access all student features</li>
          </ol>
        </div>

        <!-- Contact Info -->
        <mat-card class="contact-card">
          <mat-icon class="contact-icon">support_agent</mat-icon>
          <h4>Need Help?</h4>
          <p>Contact the school office for payment assistance</p>
        </mat-card>

        <!-- Logout Button -->
        <button mat-raised-button color="primary" class="logout-button" (click)="logout()">
          <mat-icon>logout</mat-icon>
          Logout
        </button>
      </div>
    </div>
  `,
  styleUrls: ['./student-account-locked.component.scss']
})
export class StudentAccountLockedComponent implements OnInit {
  loading = false;
  overduePayments: StudentPaymentScheduleItem[] = [];
  totalOverdueAmount = '';
  lockedAccountInfo: any = null;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit(): void {
    // Only access sessionStorage in browser environment
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // Get locked account info from session storage
    const infoStr = sessionStorage.getItem('locked_account_info');
    if (infoStr) {
      try {
        this.lockedAccountInfo = JSON.parse(infoStr);
        this.loadOverduePayments();
      } catch (e) {
        console.error('Error parsing locked account info:', e);
        this.router.navigate(['/login']);
      }
    } else {
      // No session info available - redirect back to login
      this.router.navigate(['/login']);
    }
  }

  loadOverduePayments(): void {
    this.loading = true;

    // Call special locked account endpoint with email and password
    const url = `${environment.apiBaseUrl}/api/v1/auth/locked-account/payment-info`;
    const payload = {
      email: this.lockedAccountInfo.email,
      password: this.lockedAccountInfo.password
    };

    this.http.post<any>(url, payload).subscribe({
      next: (schedule) => {
        // Map backend response to frontend payment items
        this.overduePayments = schedule.items
          .filter((item: any) => item.status === 'OVERDUE')
          .map((item: any) => this.mapPaymentItem(item));

        const total = this.overduePayments.reduce((sum, item) => sum + item.amount, 0);
        this.totalOverdueAmount = this.formatCurrency(total);
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading payment schedule:', error);
        this.loading = false;
        // Even if payment data fails to load, still show the locked message
      }
    });
  }

  // Map backend payment item to frontend format
  private mapPaymentItem(item: any): StudentPaymentScheduleItem {
    const dueDate = new Date(item.dueDate);
    const periodStart = new Date(item.periodStart);
    const periodEnd = new Date(item.periodEnd);

    return {
      id: `${item.subjectId}-${item.periodStart}`,
      subjectId: item.subjectId,
      subjectName: item.subjectName,
      offerDuration: item.offerDuration,
      dueDate: dueDate,
      periodStart: periodStart,
      periodEnd: periodEnd,
      amount: item.amount,
      baseAmount: item.baseAmount || item.amount,
      discountApplied: item.discountApplied || 0,
      prorated: item.prorated || false,
      prorataFactor: item.prorataFactor || 1,
      status: item.status,
      paidDate: item.paidDate ? new Date(item.paidDate) : undefined,
      paymentId: item.paymentId,
      paymentReference: item.paymentReference,
      paymentNote: item.paymentNote,
      formattedAmount: this.formatCurrency(item.amount),
      formattedDueDate: this.formatDate(dueDate),
      formattedPaidDate: item.paidDate ? this.formatDate(new Date(item.paidDate)) : undefined,
      formattedPeriod: this.formatPeriod(periodStart, periodEnd)
    };
  }

  private formatDate(date: Date): string {
    return new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }).format(date);
  }

  private formatPeriod(start: Date, end: Date): string {
    const startFormatted = this.formatDate(start);
    const endFormatted = this.formatDate(end);

    if (startFormatted === endFormatted) {
      return startFormatted;
    }
    return `${startFormatted} to ${endFormatted}`;
  }

  getDaysOverdue(item: StudentPaymentScheduleItem): number {
    const today = new Date();
    const dueDate = item.dueDate;
    const diffTime = today.getTime() - dueDate.getTime();
    return Math.floor(diffTime / (1000 * 3600 * 24));
  }

  formatCurrency(amount: number): string {
    // Check if amount has meaningful decimal places
    const hasDecimals = amount % 1 !== 0;

    // Format: "100 TND" or "100.50 TND"
    if (hasDecimals) {
      return `${amount.toFixed(2)} TND`;
    } else {
      return `${amount} TND`;
    }
  }

  logout(): void {
    // Clear locked account info from session storage (browser only)
    if (isPlatformBrowser(this.platformId)) {
      sessionStorage.removeItem('locked_account_info');
    }
    this.authService.logout(true);
  }
}
